import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
import { invoiceReminderFeeWrittenOff } from "store/actions/invoiceActions";
//#endregion
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import TopTodoCard from "components/todos/_partials/topTodoCard";
import {
  EntityCardContent,
  GenericCommentsView,
  PageFooter,
  Profiles,
  TitledContainerInner,
} from "components/common";
import { useInvoiceFileViewer, useInvoiceSnoozeAction, useWriteOffReminderFeeDialog } from "components/invoice";
import { useLocation, useNavigate } from "react-router-dom";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { InvoiceHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const InvoiceTodoReminderFeeNotPaid: React.FC<TodoCardProps> = (props) => {
  const invoiceService = useInvoiceService();

  const { onActionSuccess } = props;
  const invoice = props.item.invoice;

  const dispatch = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(false);

  const snoozeAction = useInvoiceSnoozeAction(invoice!.id, onActionSuccess);
  const { showWriteOffReminderFeeDialog } = useWriteOffReminderFeeDialog();
  const { fileViewerComponent } = useInvoiceFileViewer(invoice!);

  const remainingAmount = LocalizationHelperInstance.getFormattedCurrency(invoice?.leftToPay ?? 0, invoice?.currency);

  const onViewAllButtonClick = () => {
    navigate(UrlHelper.formatPath(`/Invoice/${invoice?.id}`), {
      state: { background: location },
    });
  };

  const onWaitForPaymentClick = () => {
    if (snoozeAction.actionDialog) dispatch(displayDialog(snoozeAction.actionDialog));
  };

  const onWriteOffClick = () => {
    showWriteOffReminderFeeDialog(remainingAmount, async () => {
      setLoading(true);
      try {
        await invoiceService.writeOffReminderFee(invoice!.id);
        dispatch(invoiceReminderFeeWrittenOff(invoice!.id));
        onActionSuccess();
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingOne={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.AmountRemaining,
            "",
            { amount: remainingAmount }
          )}
          descriptionHeadingTwo={
            invoice?.customerName ? (
              <Profiles.CustomerProfileLink
                id={invoice.customerId}
                name={invoice.customerName ?? ""}
                className={cssExports.titleLink}
              />
            ) : (
              LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.NoName)
            )
          }
          descriptionheadingThree={`${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.ReminderFeeNotPaidTotalSum
          )}: ${LocalizationHelperInstance.getFormattedCurrency(invoice?.totalAmount ?? 0, invoice?.currency)}`}
          footerText={`${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Common.Due_Date
          )}: ${LocalizationHelperInstance.getFormattedDate(invoice!.dueDate)}`}
          onViewAllClick={onViewAllButtonClick}
          quickActions={[snoozeAction]}
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReminderFeeNotPaidHeader)}
          subHeader={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.ReminderFeeNotPaidDescription,
            "",
            {
              invoiceNumber:
                invoice?.serialNumber !== undefined ? InvoiceHelper.formatInvoiceNumber(invoice.serialNumber) : "",
              customerName: invoice?.customerName ?? "",
              amount: remainingAmount,
            }
          )}
        >
          <div className={cssExports.reminderFeeNotPaidQuestion}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReminderFeeNotPaidQuestion)}
          </div>
        </TitledContainerInner>
      }
      infoViewer={fileViewerComponent}
      footer={
        <PageFooter
          className={cssExports.reminderFeeNotPaidFooter}
          disableSuccessMessages
          actions={[
            {
              text: TranslationKeys.Invoice.WaitForPayment,
              type: StyleEnums.DisplayType.Info,
              onClick: () => {
                onWaitForPaymentClick();
                return Promise.resolve();
              },
            },
            {
              text: `${LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Invoice.WriteOffReminderFee
              )} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Fee).toLowerCase()}`,
              type: StyleEnums.DisplayType.Success,
              onClick: () => {
                onWriteOffClick();
                return Promise.resolve();
              },
            },
          ]}
        />
      }
      loading={loading}
    >
      <GenericCommentsView parentId={invoice!.id} type={WintSuperkollAPIModel.GenericCommentType.Invoice} />
    </EntityCardContent>
  );
};

export default InvoiceTodoReminderFeeNotPaid;
